/**
 * Listing-alert auto-enable rule — single FE source of truth.
 *
 * ``listing_alert`` is client-owned. The server used to derive it from
 * whether the three basics were populated, but three separate surfaces
 * write those fields — the renter-profile wizard, the /saved alerts
 * card, and the listings search filters — and each needs to fire a
 * different analytics event for the transition it caused. A server-side
 * flip made transitions unattributable, so the rule moved here.
 *
 * Anything that decides whether alerts should turn on must go through
 * `hasAllListingAlertBasics` so the definition can't drift between
 * call sites.
 */

/** The three fields that gate listing alerts. */
export interface ListingAlertBasics {
  neighborhoodIds: string[] | null | undefined;
  maxRent: number | null | undefined;
  bedrooms: number[] | null | undefined;
}

/**
 * True when all three basics are populated.
 *
 * Empty arrays count as NOT set — a saved `[]` means "no criteria",
 * which would otherwise enable alerts that can never match anything.
 * Mirrors `Explorer.has_all_listing_alert_basics` on the BE, which is
 * still used there for the renter-profile completion flag.
 */
export function hasAllListingAlertBasics({
  neighborhoodIds,
  maxRent,
  bedrooms,
}: ListingAlertBasics): boolean {
  return (
    Array.isArray(neighborhoodIds) &&
    neighborhoodIds.length > 0 &&
    typeof maxRent === 'number' &&
    Array.isArray(bedrooms) &&
    bedrooms.length > 0
  );
}

/**
 * Surface that caused an auto-enable, for the `instant_alerts_on_auto`
 * event. Derived from the pathname so we can compare conversion across
 * entry points without threading a prop through every call site.
 */
export type ListingAlertAutoSource = 'home' | 'listings' | 'saved' | 'profile' | 'review' | 'other';

export function resolveListingAlertAutoSource(
  pathname: string | null | undefined,
): ListingAlertAutoSource {
  if (!pathname) return 'other';
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/listings')) return 'listings';
  if (pathname.startsWith('/saved')) return 'saved';
  if (pathname.startsWith('/profile')) return 'profile';
  if (pathname.startsWith('/review')) return 'review';
  return 'other';
}
